import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Chart from 'chart.js/auto';
import { formatAlarmDuration } from './alarmDuration';
import Itg from '../../utils/ITG_BLANCO.png'  
import ItgNegro from '../../utils/ITG-Logo-Negro.png'  
import Scapp from '../../utils/logo_scapp_blanco.png'  
import ScappNegro from '../../utils/logo_scapp_negro.png'  

const fmtHour = (ts) => {
  const d = new Date(Number(ts));
  const hh = String(d.getHours()).padStart(2,'0');
  const mi = String(d.getMinutes()).padStart(2,'0');
  const ss = String(d.getSeconds()).padStart(2,'0');
  return `${hh}:${mi}:${ss}`;
};

const solidColor = (c) => {
  if (!c) return 'rgb(150,150,150)';
  if (Array.isArray(c)) return c.map(solidColor);
  if (typeof c !== 'string') return 'rgb(150,150,150)';
  return c.startsWith('rgba')
    ? c.replace(/rgba\((\s*\d+\s*,\s*\d+\s*,\s*\d+)\s*,\s*[\d.]+\)/, 'rgb($1)')
    : c;
};

const normalizeColor = (color) => {
  const c = Array.isArray(color) ? color[0] : color;
  if (!c) return [150,150,150];
  if (c.startsWith('rgb')) {
    const [r,g,b] = c.replace(/^rgba?\(|\s+|\)$/g,'').split(',').map(Number);
    return [r,g,b];
  }
  if (c.startsWith('#')) {
    let h = c.slice(1);
    if (h.length===3) h = h.split('').map(x=>x+x).join('');
    const n = parseInt(h,16);
    return [(n>>16)&255,(n>>8)&255,n&255];
  }
  return [150,150,150];
};

const loadImageBase64 = (src) => new Promise((resolve,reject)=>{
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = ()=>{
    const c = document.createElement('canvas');
    c.width = img.width; c.height = img.height;
    c.getContext('2d').drawImage(img,0,0);
    resolve(c.toDataURL('image/png'));
  };
  img.onerror = reject;
  img.src = src;
});

export async function exportToPDF({ chart, darkModePDF=false, title='Tasa de Alimentación', jaula='', dia='', onProgress }) {
  if (!chart) { console.error('exportToPDF: chart null'); return; }

  // === 0) Preparación de datos XY ===
  const srcDatasets = chart.config?.data?.datasets ?? [];
  const allX = Array.from(new Set(
    srcDatasets.flatMap(ds => (ds.data ?? []).map(p => (p && typeof p==='object') ? Number(p.x) : Number(p)))
  ))
  .filter(v => Number.isFinite(v))
  .sort((a,b) => a - b);

  // Map rápido x -> y por dataset
  const maps = srcDatasets.map(ds => {
    const m = new Map();
    (ds.data ?? []).forEach(p => {
      const x = (p && typeof p==='object') ? Number(p.x) : Number(p);
      const y = (p && typeof p==='object') ? p.y : undefined;
      if (Number.isFinite(x)) m.set(x, (typeof y === 'number') ? y : null);
    });
    return m;
  });

  // === 1) Logos / colores ===
  // const itg = (typeof window!=='undefined' && window?.process?.versions?.electron)
  //   ? `${window.location.origin}/images/ITG_BLANCO.png`
  //   : process.env.PUBLIC_URL + `/images/ITG_BLANCO.png`;
  // const itgNegro = (typeof window!=='undefined' && window?.process?.versions?.electron)
  //   ? `${window.location.origin}/images/ITG-Logo-Negro.png`
  //   : process.env.PUBLIC_URL + `/images/ITG-Logo-Negro.png`;
  // const scapp = (typeof window!=='undefined' && window?.process?.versions?.electron)
  //   ? `${window.location.origin}/images/logo_scapp_blanco.png`
  //   : process.env.PUBLIC_URL + `/images/logo_scapp_blanco.png`;
  // const scappNegro = (typeof window!=='undefined' && window?.process?.versions?.electron)
  //   ? `${window.location.origin}/images/logo_scapp_negro.png`
  //   : process.env.PUBLIC_URL + `/images/logo_scapp_negro.png`;
  const itg = Itg
  const itgNegro = ItgNegro
  const scapp = Scapp
  const scappNegro = ScappNegro

  const [logoITG, logoSCAPP] = await Promise.all([
    loadImageBase64(darkModePDF ? itg : itgNegro),
    loadImageBase64(darkModePDF ? scapp : scappNegro),
  ]);

  if (onProgress) onProgress(10);
  // cantidad de columnas por tabla 
  const tablesPerPage = 5;

  // === 2) Chart temporal (XY) con 3 ejes Y ===
  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = chart.canvas.width;
  tempCanvas.height = chart.canvas.height;
  const tempCtx = tempCanvas.getContext('2d');

  const axisColor = darkModePDF ? '#ffffff' : '#000000';
  const gridColor = darkModePDF ? '#444444' : 'rgba(0,0,0,0.3)';

  const findIndexBy = (kw) => (srcDatasets.findIndex(d => (d?.label ?? '').toLowerCase().includes(kw)));
  const idxActividad = findIndexBy('actividad');
  const idxPPA       = srcDatasets.findIndex(d => (d?.label ?? '').toLowerCase() === 'ppa');
  const idxTasa      = findIndexBy('tasa');

  // Colores de referencia (caen en defaults si no se encuentran)
  const colorActividad = normalizeColor(
    srcDatasets[idxActividad]?.borderColor || srcDatasets[idxActividad]?.backgroundColor || '#2e7d32'
  );
  const colorTasa = normalizeColor(
    srcDatasets[idxTasa]?.borderColor || srcDatasets[idxTasa]?.backgroundColor || '#1565c0'
  );
  const colorPPA = normalizeColor(
    srcDatasets[idxPPA]?.borderColor || srcDatasets[idxPPA]?.backgroundColor || '#8e24aa'
  );
  const toRgb = ([r,g,b]) => `rgb(${r},${g},${b})`;

  // Asignar yAxisID por dataset según su etiqueta
  const datasetsForChart = srcDatasets.map(ds => {
    const label = (ds.label ?? '').toLowerCase();
    let yAxisID = 'yLeft'; // default
    if (label.includes('tasa')) yAxisID = 'yRightTasa';
    if (label === 'ppa' || label.includes('ppa')) yAxisID = 'yRightPPA';
    const bg = solidColor(ds.backgroundColor);
    const bc = ds.borderColor || bg;
    return {
      ...ds,
      type: ds.type || 'line',
      parsing: false,
      fill: ds.fill ?? false,
      backgroundColor: bg,
      borderColor: bc,
      yAxisID,
    };
  });

  // Ticks del eje X en HH:mm
  const fmtTickHour = (v) => {
    const d = new Date(Number(v));
    const hh = String(d.getHours()).padStart(2,'0');
    const mi = String(d.getMinutes()).padStart(2,'0');
    return `${hh}:${mi}`;
  };

  const tempChart = new Chart(tempCtx, {
    type: 'line',
    data: { datasets: datasetsForChart },
    options: {
      responsive: false,
      animation: false,
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
      scales: {
        x: {
          type: 'linear',
          grid: { color: gridColor },
          ticks: {
            color: axisColor,
            callback: fmtTickHour,
            maxRotation: 0,
            autoSkip: true,
          }
        },
        // Izquierda: Actividad
        yLeft: {
          position: 'left',
          grid: { color: gridColor },
          ticks: {
            color: toRgb(colorActividad),
            callback: (v) => `${v}`, // añade '%' si lo deseas
          },
          title: {
            display: true,
            text: 'Actividad [%]',
            color: toRgb(colorActividad),
          }
        },
        // Derecha 1: Tasa
        yRightTasa: {
          position: 'right',
          grid: { drawOnChartArea: false }, // sin segunda grilla
          ticks: { color: toRgb(colorTasa), callback: (v) => `${v}` },
          title: { display: true, text: 'Tasa [PPM]', color: toRgb(colorTasa) },
          // offset false (pegado al borde derecho)
          offset: false,
        },
        // Derecha 2: PPA (desfasado para no pisar a Tasa)
        yRightPPA: {
          position: 'right',
          grid: { drawOnChartArea: false },
          ticks: { color: toRgb(colorPPA), callback: (v) => `${v}` },
          title: { display: true, text: 'PPA', color: toRgb(colorPPA) },
          // offset true para que sus labels/axis queden paralelos a yRightTasa
          offset: true,
        },
      },
    },
  });

  await new Promise(r=>setTimeout(r,50));
  const imgData = tempCanvas.toDataURL('image/png');
  if (onProgress) onProgress(30);

    // === 3) PDF primera página ===
    const pdf = new jsPDF({ orientation:'landscape', unit:'pt', format:'a4' });
    const W = pdf.internal.pageSize.getWidth();
    const H = pdf.internal.pageSize.getHeight();
    const pageBg = darkModePDF ? '#1e1e1e' : '#ffffff';
    const textColor = darkModePDF ? '#ffffff' : '#000000';

    const HEADER_H = 60, PAD = 12;


    const paintHeader = () => {
      const itgW = 70, scappW = 120;
      const itgY = (HEADER_H - 40)/2;
      const scappY = (HEADER_H - 30)/2 + 9;
      pdf.addImage(logoITG, 'PNG', 40, itgY, itgW, 0);
      pdf.addImage(logoSCAPP, 'PNG', W - scappW - 40, scappY, scappW, 0);
    };

    pdf.setFillColor(pageBg); pdf.rect(0,0,W,H,'F');
    paintHeader();

    // Título: “Tasa de Alimentación [PPM] — {jaula} — Día: {dia}”
    pdf.setFont('helvetica','bold'); pdf.setFontSize(16); pdf.setTextColor(textColor);
    const composedTitle = `${title} — ${jaula}${dia ? ` — Día: ${dia}` : ''}`;
    pdf.text(composedTitle, W/2, 75, { align:'center' });

  // Leyenda
    const datasets = chart.config.data?.datasets ?? [];
    const legendY = 95;
    const imgW = W * 0.8;
    const imgH = tempCanvas.height * (imgW / tempCanvas.width);
    const posX = (W - imgW)/2;
    const boxW = imgW + PAD*2;
    const boxX = posX - PAD;

    const spacingX = 90, spacingY = 22, maxPerRow = 7;
    const items = datasets.filter(ds => !!ds.label);
    const rows = Math.ceil(items.length / maxPerRow);
    const legendBoxH = rows * spacingY + 18;

    pdf.setFillColor(darkModePDF ? '#2b2b2b' : '#f5f5f5');
    pdf.setDrawColor(darkModePDF ? '#444' : '#ccc');
    pdf.roundedRect(boxX, legendY, boxW, legendBoxH, 6,6,'FD');

    let i = 0;
    items.forEach(ds=>{
      const row = Math.floor(i / maxPerRow);
      const col = i % maxPerRow;
      const totalRow = Math.min(items.length - row*maxPerRow, maxPerRow);
      const rowWidth = totalRow * spacingX;
      const startX = boxX + PAD + (boxW - PAD*2 - rowWidth)/2;
      const x = startX + col*spacingX;
      const y = legendY + 20 + row*spacingY;

      const [r,g,b] = normalizeColor(ds.backgroundColor || ds.borderColor || '#999');
      pdf.setFillColor(r,g,b); pdf.rect(x,y,12,12,'F');
      pdf.setTextColor(darkModePDF ? '#fff' : '#000'); pdf.setFontSize(10);
      pdf.text(ds.label, x + 18, y + 10);
      i++;
    });

    const chartY = legendY + legendBoxH + 20;
    pdf.setFillColor(darkModePDF ? '#2b2b2b' : '#f5f5f5');
    pdf.setDrawColor(darkModePDF ? '#444' : '#ccc');
    pdf.roundedRect(posX - PAD, chartY - PAD, imgW + PAD*2, imgH + PAD*2, 6,6,'FD');
    pdf.addImage(imgData, 'PNG', posX, chartY, imgW, imgH);

   if (onProgress) onProgress(50);
    // === Header y marca de agua ===
    // === FLAGS ===
    const WATERMARK_FIRST_PAGE = false; 
    // ? === 4) Tablas separadas ===
    const TABLE_TOP = HEADER_H + 20;
    const idxAlarmas   = findIndexBy('alarmas');

    // mapas x->y construidos antes: `maps` (en el mismo orden que `datasets`)
    const getVal = (idx, x) => (idx >= 0 ? maps[idx].get(x) : undefined);
    const fmtNum = (v) => (typeof v === 'number' ? (Number.isInteger(v) ? v : v.toFixed(2)) : '');
    
    const totalTables = 3;
    let currentTable = 0;

    const drawTitledTable = (title, head, body) => {

      // === Configuración base (aprovecha variables ya definidas arriba) ===
      const borderColor = darkModePDF ? '#ffffff' : '#000000';
      const pageBg = darkModePDF ? '#1e1e1e' : '#ffffff';
      const textColorLocal = darkModePDF ? '#ffffff' : textColor;

      // Layout general
      const TOP = TABLE_TOP + 6;     // Y de inicio real de tablas
      const LEFT_M = 40;             // margen izq
      const RIGHT_M = 40;            // margen der
      const BOT_M = 40;              // margen inf
      const GAP_COL = 16;            // espacio entre tablas (columnas)
      const TITLE_H = 18;            // altura visual del título de página

      // Tipografía y tamaños que usaremos en la tabla para calcular alturas
      const FONT = 'helvetica';
      const HEADER_FONT_SIZE = 10;
      const BODY_FONT_SIZE = 8;
      const LINE_HEIGHT = 1.25;      // línea un poco alta para legibilidad
      const ROW_H = Math.ceil(BODY_FONT_SIZE * LINE_HEIGHT) + 6; // alto aproximado por fila (+padding)

      // === 1) Medición de ancho mínimo para etiqueta y definición de anchos ===
      // head es como: [['Etiqueta', 'Tasa [ PPM ]']] o [['Etiqueta','Actividad [ % ]','PPA']]
      const nCols = head[0].length;
      const labelColIndex = 0;
      const valueCols = nCols - 1;

      // Prepara fuentes para medir
      pdf.setFont(FONT, 'normal');
      pdf.setFontSize(BODY_FONT_SIZE);

      // El texto más largo de la primera columna (etiqueta)
      const longestLabel = body.reduce((m, r) => {
        const t = String(r[labelColIndex] ?? '');
        return (t.length > m.length) ? t : m;
      }, String(head[0][labelColIndex] ?? 'Etiqueta'));

      // Ancho en puntos + paddings internos
      const labelTextW = pdf.getTextWidth(longestLabel);
      const LABEL_PAD = 16; // padding horizontal total razonable
      const MIN_LABEL_W = Math.max(60, Math.ceil(labelTextW + LABEL_PAD));

      // Define ancho fijo para columnas de valores (números/strings cortos)
      const VALUE_W = 70; // si necesitas más compactación, baja a 64 o 60

      // Ancho mínimo total de una tabla-columna
      const tableMinW = MIN_LABEL_W + valueCols * VALUE_W;

      // === 2) Cálculo de cuántas columnas caben por página ===
      const pageW = W;
      const pageH = H;

      // Caja de contenido horizontal disponible
      const contentW = pageW - LEFT_M - RIGHT_M;

      // número máximo de columnas por página (al menos 1)
      const maxColsPerPage = Math.max(1, Math.floor((contentW + GAP_COL) / (tableMinW + GAP_COL)));

      // Ajuste de separación para centrar visualmente el bloque de columnas
      const totalBlockW = maxColsPerPage * tableMinW + (maxColsPerPage - 1) * GAP_COL;
      const startXBase = LEFT_M + Math.max(0, Math.floor((contentW - totalBlockW) / 2));

      // === 3) Cálculo de cuántas filas caben por columna ===
      // Altura disponible vertical: desde TOP hasta (H - BOT_M)
      // Considera altura del header de la tabla (aprox) + espacio de título
      const approxHeadH = Math.ceil(HEADER_FONT_SIZE * LINE_HEIGHT) + 10; // cabecera de la tabla
      const availableH = pageH - TOP - BOT_M - TITLE_H;
      const rowsPerCol = Math.max(1, Math.floor((availableH - approxHeadH) / ROW_H));

      const drawTableChunkAt = (x, y, chunkRows, showHead = true) => {
        autoTable(pdf, {
          head: showHead ? head : [],
          body: chunkRows,
          startY: y,
          margin: { left: x, right: RIGHT_M, top: TOP, bottom: BOT_M },
          tableWidth: tableMinW,
          styles: {
            font: FONT,
            fontSize: BODY_FONT_SIZE,
            halign: 'center',
            valign: 'middle',
            lineWidth: 0.25,
            lineColor: borderColor,
            textColor: textColorLocal,
            cellPadding: 3,
            fillColor: undefined,          // 👈 evita pintar celdas con fondo por default
          },
          headStyles: {
            font: FONT,
            fontStyle: 'bold',
            fontSize: HEADER_FONT_SIZE,
            fillColor: '#e84e0e',          // 👈 encabezado naranja
            textColor: '#ffffff',
            halign: 'center',
            lineColor: borderColor,
            lineWidth: 0.5,
          },
          columnStyles: {
            [labelColIndex]: { cellWidth: MIN_LABEL_W, halign: 'left' },
            ...Object.fromEntries(
              Array.from({ length: valueCols }, (_, i) => [i + 1, { cellWidth: VALUE_W }])
            ),
          },
          theme: 'grid',
          pageBreak: 'avoid',
          willDrawPage: () => {
            // if (darkModePDF) { pdf.setFillColor(pageBg); pdf.rect(0,0,W,H,'F'); }
            paintHeader();
            pdf.setFont(FONT, 'bold');
            pdf.setFontSize(14);
            pdf.setTextColor(textColorLocal);
            pdf.text(title, W/2, TABLE_TOP - 8, { align: 'center' });
          },
          didParseCell: (data) => {
            // 👇 Si estamos en darkMode, el cuerpo no debe tener fillColor
            if (darkModePDF && data.section === 'body') {
              data.cell.styles.fillColor = undefined;
            }
          },
        });
      };


      // === 5) Render en columnas por página ===
      let remaining = body.slice();
      let firstPageForThisTable = true;

      // Límite efectivo para esta tabla (si no viene en opts, usa el global de exportToPDF)
      const MAX_TABLES_PER_PAGE = Math.max(1, Number(tablesPerPage));

      while (remaining.length > 0) {
        // Prepara página (si es la primera vez que entramos para esta tabla)
        if (firstPageForThisTable) {
          pdf.addPage();
          if (darkModePDF) { pdf.setFillColor(pageBg); pdf.rect(0, 0, W, H, 'F'); }
          paintHeader();
          pdf.setFont(FONT, 'bold');
          pdf.setFontSize(14);
          pdf.setTextColor(textColorLocal);
          pdf.text(title, W / 2, TABLE_TOP - 8, { align: 'center' });
          firstPageForThisTable = false;
        }

        // 🔑 cuántas columnas caben en esta página y cuántas voy a usar de verdad
        // const colsThisPage = Math.min(maxColsPerPage, Math.ceil(remaining.length / rowsPerCol));
        const colsThisPage = Math.min(
          MAX_TABLES_PER_PAGE,
          maxColsPerPage,
          Math.ceil(remaining.length / rowsPerCol)
        );

        const blockW = colsThisPage * tableMinW + (colsThisPage - 1) * GAP_COL;

        let x = LEFT_M + Math.max(0, Math.floor((contentW - blockW) / 2));
        let y = TOP + TITLE_H;

        // for (let c = 0; c < colsThisPage && remaining.length > 0; c++) {
        //   const colRows = remaining.splice(0, rowsPerCol);
        //   drawTableChunkAt(x, y, colRows, true);
        //   x += tableMinW + GAP_COL;
        // }

        // // Si aún quedan filas, la siguiente iteración creará otra página y seguirá
        // if (remaining.length > 0) {
        //   // La siguiente vuelta del while añadirá página y título
        //   continue;
        // }
        for (let c = 0; c < colsThisPage && remaining.length > 0; c++) {
          const colRows = remaining.splice(0, rowsPerCol);
          drawTableChunkAt(x, y, colRows, true);
          x += tableMinW + GAP_COL;
        }

        // 👇 si aún hay filas pendientes, fuerza nueva página
        if (remaining.length > 0) {
          pdf.addPage();
          if (darkModePDF) { pdf.setFillColor(pageBg); pdf.rect(0, 0, W, H, 'F'); }
          paintHeader();
          pdf.setFont(FONT, 'bold');
          pdf.setFontSize(14);
          pdf.setTextColor(textColorLocal);
          pdf.text(title, W / 2, TABLE_TOP - 8, { align: 'center' });
        }

      }

      currentTable++;
      if (onProgress) {
        const percent = 50 + Math.round((currentTable / totalTables) * 40);
        onProgress(percent);
      }
    }

    const filterEmptyRows = (body) =>
        body.filter(row => row.slice(1).some(v => v !== '' && v !== null && v !== undefined));

        // ===== Tabla 1: Alarmas =====
    // 1) Preferimos la fuente “oficial” si la guardaste en la instancia: chart._alarmIntervals
    // 2) Si no existe, intentamos leer desde el dataset “Alarmas” (cada punto puede traer .raw.alarmData)
    let alarmRows = [];
    if (Array.isArray(chart._alarmIntervals) && chart._alarmIntervals.length) {
      alarmRows = chart._alarmIntervals.map(a => ({
        start: +new Date(a.start),
        duration: a.duration ?? '-',
        max: a.detectados_max ?? '-',
        total: a.detectados_total ?? '-',
      }));
    } else if (idxAlarmas >= 0) {
      const ds = datasets[idxAlarmas];
      alarmRows = (ds.data ?? [])
        .map(p => (p?.alarmData ? p.alarmData : p?.raw?.alarmData))
        .filter(Boolean)
        .map(a => ({
          start: +new Date(a.start),
          duration: a.duration ?? '-',
          max: a.detectados_max ?? '-',
          total: a.detectados_total ?? '-',
        }));
    }

    // si hay alarmas, dibujamos la tabla
    if (alarmRows.length) {
      // orden por inicio
      alarmRows.sort((a,b) => a.start - b.start);

      const head = [['Inicio', 'Duración [s]', 'Det. Máximas', 'Det. Totales']];
      // const body = alarmRows.map(a => [fmtTime(a.start), a.duration, a.max, a.total]);
      const body = alarmRows.map(a => [fmtHour(a.start), formatAlarmDuration(a.duration), a.max, a.total]);


      drawTitledTable(`Alarmas — ${jaula}${dia ? ` — Día: ${dia}` : ''}`, head, filterEmptyRows(body), { tablesPerPage });

    }


    // ===== Tabla 2: Actividad + PPA =====
    if (idxActividad >= 0 || idxPPA >= 0) {
      const head = [[
        'Hora',
        ...(idxActividad >= 0 ? ['Actividad [%]'] : []),
        ...(idxPPA >= 0 ? ['PPA'] : []),
      ]];

      const body = allX.map(x => {
        const row = [fmtHour(x)];
        if (idxActividad >= 0) row.push(fmtNum(getVal(idxActividad, x)));
        if (idxPPA >= 0)       row.push(fmtNum(getVal(idxPPA, x)));
        return row;
      });
      // 👇 ordena ascendente por hora
      body.sort((a, b) => (a[0] > b[0] ? 1 : -1));


      drawTitledTable(`Actividad [ % ] y PPA — ${jaula}${dia ? ` — Día: ${dia}` : ''}`, head, filterEmptyRows(body), { tablesPerPage });
    }

    // ===== Tabla 3: Tasa [PPM] =====
    if (idxTasa >= 0) {
      const head = [['Hora', 'Tasa [ PPM ]']];
      // const body = allX.map(x => [fmtTime(x), fmtNum(getVal(idxTasa, x))]);
      const body = allX.map(x => [fmtHour(x), fmtNum(getVal(idxTasa, x))]);
      // 👇 ordena ascendente por hora
      body.sort((a, b) => (a[0] > b[0] ? 1 : -1));

      drawTitledTable(`Tasa de Alimentación [ PPM ] — ${jaula}${dia ? ` — Día: ${dia}` : ''}`, head, filterEmptyRows(body), { tablesPerPage });
    }


  // numeración + marca de agua suave (opcional)
  const total = pdf.internal.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    pdf.setPage(p);
    pdf.setFont('helvetica','normal'); pdf.setFontSize(10); pdf.setTextColor(textColor);
    pdf.text(`${p} / ${total}`, W - 40, H - 24, { align:'right' });
  }


  const stampAllPages = (pdf, watermarkImg, darkModePDF, WATERMARK_FIRST_PAGE) => {
      const total = pdf.internal.getNumberOfPages();

      for (let i = 1; i <= total; i++) {
        pdf.setPage(i);

        const w = pdf.internal.pageSize.getWidth();
        const h = pdf.internal.pageSize.getHeight();

        // --- Marca de agua ---
        if (i > 1 || WATERMARK_FIRST_PAGE) {
          const wmW = Math.min(500, w * 0.65);
          const wmX = (w - wmW) / 2;
          const wmY = h / 2 - wmW * 0.38;

          pdf.setGState(new pdf.GState({ opacity: 0.08 }));
          pdf.addImage(watermarkImg, "PNG", wmX, wmY, wmW, 0);
          pdf.setGState(new pdf.GState({ opacity: 1 }));
        }

        // --- Número de página ---
        const label = `${i} / ${total}`;
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(10);
        pdf.setTextColor(darkModePDF ? "#ffffff" : "#000000");

        pdf.text(label, w - 40, h - 24, { align: "right" });
      }
  };

  stampAllPages(pdf, logoITG, darkModePDF, WATERMARK_FIRST_PAGE);
  if (onProgress) onProgress(100);
  
  pdf.save('TasaAlimentacion.pdf');
  tempChart.destroy();
}
